import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import type { IntegrationAttemptDto } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { DateTimeText } from "components/DateTimeText/DateTimeText";
import { EmptyValue } from "components/MutedPlaceholder";
import { IntegrationAttemptStatusChip } from "../components/IntegrationAttemptStatusChip";

export interface IntegrationAttemptsListProps {
  attempts: IntegrationAttemptDto[];
}

function NoAttemptsEmptyState() {
  return (
    <Stack spacing={1} sx={{ alignItems: "center", py: 6 }}>
      <Typography variant="subtitle1">This account has never been sent to an external system.</Typography>
      <Typography variant="body2" color="text.secondary">
        Use Push to Zuora on the account summary to create or update it there.
      </Typography>
    </Stack>
  );
}

// A rejection runs to a paragraph, which the default tooltip renders at 11px in a 300px column.
const detailTooltipSlotProps = {
  tooltip: {
    sx: { maxWidth: 440, p: 1.25, fontSize: 13, lineHeight: 1.6, wordBreak: "break-word" },
  },
};

// A failed attempt carries both a reason and a failure, and the row is one line high, so the
// failure takes the line and the reason moves into the tooltip.
function DetailCell({ attempt }: { attempt: IntegrationAttemptDto }) {
  const { reason, failureDetail } = attempt;
  const text = failureDetail ?? reason;

  if (!text) {
    return <EmptyValue />;
  }

  const tooltip =
    failureDetail && reason ? (
      <>
        {failureDetail}
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: "1px solid",
            borderColor: "currentColor",
            opacity: 0.6,
          }}
        >
          Reason given: {reason}
        </Box>
      </>
    ) : (
      text
    );

  return (
    <Tooltip title={tooltip} slotProps={detailTooltipSlotProps}>
      <span>{text}</span>
    </Tooltip>
  );
}

const columns: GridColDef<IntegrationAttemptDto>[] = [
  {
    field: "system",
    headerName: "System",
    minWidth: 110,
    sortable: false,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    sortable: false,
    renderCell: (params) => (
      <Stack direction="row" sx={{ height: "100%", alignItems: "center" }}>
        <IntegrationAttemptStatusChip attempt={params.row} />
      </Stack>
    ),
  },
  {
    field: "startedAt",
    headerName: "Started",
    minWidth: 260,
    sortable: false,
    renderCell: (params) => (
      <DateTimeText dateTime={DateTime.fromISO(params.row.startedAt)} labelFormat="dd MMM yyyy, hh:mm:ss a" />
    ),
  },
  {
    field: "trigger",
    headerName: "Trigger",
    minWidth: 140,
    sortable: false,
  },
  {
    field: "startedByUser",
    headerName: "By",
    minWidth: 180,
    sortable: false,
  },
  {
    field: "failureDetail",
    headerName: "Detail",
    flex: 1,
    minWidth: 280,
    sortable: false,
    renderCell: (params) => <DetailCell attempt={params.row} />,
  },
];

export function IntegrationAttemptsList({ attempts }: IntegrationAttemptsListProps) {
  if (attempts.length === 0) {
    return <NoAttemptsEmptyState />;
  }

  return (
    <DataTable
      rows={attempts}
      columns={columns}
      // The attempt's own id: two pushes on one account can start in the same instant, and the
      // grid needs to tell them apart.
      getRowId={(row) => row.id}
      pagingMode="client"
      sortingMode="client"
      pageSize={10}
      pageSizeOptions={[10, 20]}
    />
  );
}
